import React, { useState, useContext } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

// Material-UI components
import {
  Box,
  Modal,
  Card,
  CardContent,
  Typography,
  CardActions,
  Button,
} from "@mui/material";
// Removed unused icons imports

// Page components
import DashboardLayout from "./layouts/DashboardLayout";
import UploadPage from "./pages/UploadPage/UploadPage";
import ConfirmPage from "./pages/ConfirmPage/ConfirmPage";
import ExportPage from "./pages/ExportPage/ExportPage";
import LoginPage from "./pages/LoginPage/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";

// Auth components
import { AuthProvider, AuthContext } from "./contexts/AuthContext";
import { AllDataProvider } from "./contexts/AllDataContext";
import { DirtyStateContext, DirtyStateProvider } from "./contexts/DirtyStateContext";
import ProtectedRoute from "./components/ProtectedRoute";

type FlowState =
  | "initial"
  | "processing"
  | "generalExtracted"
  | "detailedProcessing"
  | "detailedExtracted"
  | "saving"
  | "saved"
  | "error";
// --- メインアプリケーションのレイアウトとルーティング ---
const MainApp: React.FC = () => {
  const [flowState, setFlowState] = useState<FlowState>("initial");
  const [uploadPageKey, setUploadPageKey] = useState(0); // Key for resetting UploadPage
  const [showNavigateConfirm, setShowNavigateConfirm] =
    useState<boolean>(false);
  const [nextPath, setNextPath] = useState<string>("");
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const dirtyStateContext = useContext(DirtyStateContext);

  if (!authContext || !dirtyStateContext) {
    return null; // Or some loading/error state
  }
  const { logout, isAdmin } = authContext;
  const { isDirty } = dirtyStateContext;

  const handleNavigate = (path: string) => {
    // 管理者は通常フローを持たないため、確認なしで移動
    if (isAdmin) {
      navigate(path);
      return;
    }

    const shouldBlockByFlow = [
      "processing",
      "generalExtracted",
      "detailedProcessing",
      "detailedExtracted",
      "saving",
    ].includes(flowState);

    if (shouldBlockByFlow || isDirty) {
      setNextPath(path);
      setShowNavigateConfirm(true);
    } else {
      // If navigating away from a final state, reset the flow state and the component.
      if (flowState === "saved" || flowState === "error") {
        setFlowState("initial");
        setUploadPageKey(prevKey => prevKey + 1);
      }
      navigate(path);
    }
  };

  const handleLogout = () => {
    // 管理者は通常フローを持たないため、確認なしでログアウト
    if (isAdmin) {
      logout();
      navigate('/login');
      return;
    }

    const shouldBlockByFlow = [
      "processing",
      "generalExtracted",
      "detailedProcessing",
      "detailedExtracted",
      "saving",
    ].includes(flowState);

    if (shouldBlockByFlow || isDirty) {
      setNextPath('/logout'); // Special path for logout
      setShowNavigateConfirm(true);
    } else {
      logout();
      // If we are logging out from a 'saved' state, also reset the upload page.
      if (flowState === "saved" || flowState === "error") {
        setFlowState("initial");
        setUploadPageKey(prevKey => prevKey + 1);
      }
      navigate('/login');
    }
  };

  return (
    <Box sx={{ display: "flex", height: '100vh' }}>
      <DashboardLayout handleNavigate={handleNavigate} handleLogout={handleLogout} />
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Routes>
          {/* Admin Route */}
          <Route element={<ProtectedRoute adminOnly={true} />}>
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          </Route>

          {/* Standard User Routes */}
          <Route
            path="/"
            element={
              <UploadPage
                key={uploadPageKey} // Add key to force remount
                flowState={flowState}
                setFlowState={setFlowState}
                handleNavigate={handleNavigate}
              />
            }
          />
          <Route path="/confirm" element={<ConfirmPage />} />
          <Route path="/export" element={<ExportPage />} />

          {/* Fallback route for authenticated users */}
          <Route path="*" element={<Navigate to={isAdmin ? "/admin/dashboard" : "/"} replace />} />
        </Routes>
      </Box>
      <Modal
        open={showNavigateConfirm}
        onClose={() => setShowNavigateConfirm(false)}
        aria-labelledby="navigation-confirm-title"
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            p: 3,
            borderRadius: "12px",
            outline: "none", // Prevent flicker
            "::before": {
              content: '""',
              position: "absolute",
              inset: "4px",
              border: "2px solid rgba(252, 174, 97, 1)",
              pointerEvents: "none",
              borderRadius: "12px",
            },
          }}
        >
          <CardContent>
          <Box
            sx={{
              p: 0,
              mb: 2,
              pb: 1,
              borderBottom: "2px solid rgba(252, 174, 97, 1)",
              width: "calc(100% - 2px)",
              mx: "auto",
            }}
          >
            <Typography
              id="navigation-confirm-title"
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ textAlign: "center", color: "rgba(255, 145, 55, 1)" }}
            >
              {isDirty ? "未保存の変更" : "処理が完了していません"}
            </Typography>
          </Box>
            <Typography>
              {isDirty
                ? "編集中のデータが保存されていません。移動すると変更は失われますが、よろしいですか？"
                : "現在の処理を中断してページを移動しますか？中断した場合、ここまでの作業内容は失われます。"}
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end", p: 0, pt: 2, gap: 2 }}>
            <Button
              onClick={() => {
                if (nextPath) {
                  // Reset state and remount component before navigating
                  setFlowState("initial");
                  setUploadPageKey(prevKey => prevKey + 1);

                  if (nextPath === '/logout') {
                    logout();
                    navigate('/login');
                  } else {
                    navigate(nextPath);
                  }
                }
                setShowNavigateConfirm(false);
              }}
              variant="contained"
              sx={{
                width: 110,
                whiteSpace: "nowrap",
                fontWeight: "bold",
                fontSize: "16px",
                pl: 4,
                pr: 4,
                backgroundColor: "rgba(255, 145, 55, 1)",
                border: "4px solid rgba(240, 232, 249, 0.9)",
                borderRadius: "25px",
                boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                "&:hover": {
                  backgroundColor: "rgba(255, 145, 55, 1)",
                  border: "4px solid rgba(240, 232, 249, 0.9)",
                  borderRadius: "25px",
                },
              }}
            >
              移動する
            </Button>
            <Button
              onClick={() => setShowNavigateConfirm(false)}
              variant="contained"
              sx={{
                width: 110,
                whiteSpace: "nowrap",
                fontWeight: "bold",
                fontSize: "16px",
                pl: 4,
                pr: 4,
                color: "#fff",
                backgroundColor: "#9e9e9e",
                border: "4px solid rgba(240, 232, 249, 0.9)",
                borderRadius: "25px",
                boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
                "&:hover": {
                  backgroundColor: "#9e9e9e",
                  color: "#fff",
                  border: "4px solid rgba(240, 232, 249, 0.9)",
                  borderRadius: "25px",
                },
              }}
            >
              キャンセル
            </Button>
          </CardActions>
        </Card>
      </Modal>
    </Box>
  );
};

// --- イベントリスナーとルーティングを含む新しいコンポーネント ---
const AppContent: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/*" element={<MainApp />} />
        </Route>
      </Routes>
    </Router>
  );
};



// --- アプリケーション全体のエントリーポイント ---
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AllDataProvider>
        <DirtyStateProvider>
          <AppContent />
        </DirtyStateProvider>
      </AllDataProvider>
    </AuthProvider>
  );
};

export default App;
